import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal,
  forwardRef,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { INPUT_SEARCH_HOST } from '../inputs.constants';
import {
  Overlay,
  OverlayConfig,
  OverlayModule,
  OverlayRef,
  PositionStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Observable, fromEvent, map, take, tap } from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'div[inputSearch]',
  standalone: true,
  imports: [OverlayModule],
  host: INPUT_SEARCH_HOST,
  template: `<div class="input-search__input-wrapper">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        class="input-search__icon"
      >
        <g transform="translate(0)">
          <path
            d="M19.816,18.912l-5.164-5.081a8.167,8.167,0,0,0,2.183-5.55A8.349,8.349,0,0,0,8.42,0,8.348,8.348,0,0,0,.006,8.281,8.348,8.348,0,0,0,8.42,16.561a8.462,8.462,0,0,0,5.3-1.849l5.184,5.1a.654.654,0,0,0,.915,0A.63.63,0,0,0,19.816,18.912ZM8.42,15.287A7.064,7.064,0,0,1,1.3,8.281,7.064,7.064,0,0,1,8.42,1.274a7.064,7.064,0,0,1,7.12,7.007A7.064,7.064,0,0,1,8.42,15.287Z"
            transform="translate(-0.006)"
            fill="#199e46"
          />
        </g>
      </svg>
      <input
        type="text"
        [attr.id]="id() || null"
        autocomplete="off"
        class="input-search__field"
        [attr.placeholder]="this.placeholder() || null"
        (input)="handleInput($event)"
        (focus)="handleInputFocus()"
        (blur)="handleBlur()"
        (keydown)="handleKeydown($event)"
      />
    </div>
    <ng-template #suggestion>
      @if (this.suggestionItemList().length) {
        <div class="input-search__suggestion-wrapper">
          <ul class="suggestion-list">
            @for (item of suggestionItemList(); track $index) {
              <li class="suggestion-option">
                <span class="suggestion-option__icon">
                  @if (textField.value) {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14.773"
                      height="14.773"
                      viewBox="0 0 14.773 14.773"
                    >
                      <g>
                        <g>
                          <path
                            d="M14.639,13.97l-3.814-3.753a6.033,6.033,0,0,0,1.613-4.1A6.167,6.167,0,0,0,6.221,0,6.166,6.166,0,0,0,.006,6.117a6.166,6.166,0,0,0,6.216,6.117,6.251,6.251,0,0,0,3.912-1.366l3.829,3.768a.483.483,0,0,0,.676,0A.466.466,0,0,0,14.639,13.97ZM6.221,11.292A5.218,5.218,0,0,1,.962,6.117,5.218,5.218,0,0,1,6.221.941a5.218,5.218,0,0,1,5.259,5.176A5.218,5.218,0,0,1,6.221,11.292Z"
                            transform="translate(-0.006)"
                            fill="currentColor"
                          />
                        </g>
                      </g>
                    </svg>
                  } @else {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14.264"
                      height="14.264"
                      viewBox="0 0 14.264 14.264"
                    >
                      <path
                        d="M152.219,152.219a.644.644,0,0,1,.922,0l2.229,2.229a.652.652,0,0,1-.922.922l-2.229-2.229a.644.644,0,0,1,0-.922Z"
                        transform="translate(-147.79 -147.79)"
                        fill="currentColor"
                      />
                      <path
                        d="M6.24.891A.891.891,0,0,1,7.132,0,7.132,7.132,0,1,1,2.141,2.038.892.892,0,1,1,3.388,3.312,5.348,5.348,0,1,0,8.023,1.857v.818a.891.891,0,0,1-1.783,0Z"
                        fill="currentColor"
                        opacity="0.4"
                      />
                    </svg>
                  }
                </span>
                <span
                  class="suggestion-option__label"
                  [innerHTML]="item"
                ></span>
              </li>
            }
          </ul>
        </div>
      }
    </ng-template>`,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputSearchComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  private _platformId = inject(PLATFORM_ID);

  @Output() enterKeyPress: EventEmitter<void> = new EventEmitter<void>();
  @Output() suggestionSelect: EventEmitter<string> = new EventEmitter<string>();

  placeholder = input<string | undefined | null>(null);
  inputId = input.required<string>();
  suggestions = input<string[]>([]);

  suggestionTemplate = viewChild<TemplateRef<unknown>>('suggestion');
  inputFieldRef = viewChild<ElementRef<HTMLInputElement>>('inputField');

  protected suggestionItemList: WritableSignal<string[]> = signal([]);
  protected id = signal('');
  protected isFocused = signal(false);

  private suggestionPortal: TemplatePortal<unknown> | undefined;
  private overlayRef: OverlayRef | undefined;

  onChange: (value: unknown) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _overlay: Overlay,
    private _injector: Injector,
    private _destroyRef: DestroyRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _renderer: Renderer2
  ) {}

  get host(): HTMLElement {
    return this._viewContainerRef.element.nativeElement as HTMLElement;
  }

  get textField(): HTMLInputElement {
    return this.host.querySelector('.input-search__field') as HTMLInputElement;
  }

  private get hostFormField(): HTMLElement | null {
    return this.host.closest('.form-field');
  }

  private get inputEvent(): InputEvent {
    const event = new InputEvent('input', { cancelable: true, bubbles: false });
    return event;
  }

  private get _formattedSuggestions(): string[] {
    return this.suggestions().map(item => {
      return `${item.substring(0, this.textField.value.length)}<b>${item.substring(this.textField.value.length)}</b>`;
    });
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  private get _ngControl(): NgControl | null {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });

    return ngControl;
  }

  ngOnInit(): void {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('suggestions' in changes) {
      this.suggestionItemList.set(this._formattedSuggestions);
      if (this.isFocused()) {
        this._showSuggestion();
      }
    }

    if ('inputId' in changes) {
      if (!this.inputId().length || this.inputId().includes(' ')) {
        throw new Error('inputId must not be empty or contain spaces.');
      } else {
        this.id.set(this.inputId());
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.hostFormField) {
      throw new Error(
        'To ensure proper functionality, InputSearchComponent should be hosted within a FormFieldComponent.'
      );
    }

    this.suggestionPortal = new TemplatePortal(
      this.suggestionTemplate()!,
      this._viewContainerRef
    );
  }

  writeValue(value: string | null | undefined): void {
    this.textField.value = value || '';
    value
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.textField.setAttribute('disabled', 'true');
      this._addClassToHostElement('input-disabled');
    } else {
      this.textField.removeAttribute('disabled');
      this._removeClassFromHostElement('input-disabled');
    }
  }

  protected closeSuggestion(): void {
    if (!this.overlayRef) return;
    this.overlayRef.detach();
    this.overlayRef.dispose();
    this.overlayRef = undefined;
  }

  clearInput(): void {
    this.textField.value = '';
    this.textField.dispatchEvent(this.inputEvent);
    this._removeClassFromHostElement('input-filled');
    this.textField.focus();
  }

  focusInput(): void {
    this.textField.focus();
  }

  protected handleInputFocus(): void {
    this.isFocused.set(true);
    this._addClassToHostElement('input-focused');
    if (this.suggestions().length) {
      this._showSuggestion();
    }
  }

  protected handleBlur(): void {
    this.onTouched();
    this.isFocused.set(false);
    if (this._isPlatformBrowser) {
      if (!document.hasFocus()) {
        this._removeClassFromHostElement('input-focused');
        this.closeSuggestion();
        return;
      }
      this._handleDocumentClick();
    }
  }

  protected handleInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onChange(value);
    value
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');

    this.suggestionItemList.set(this._formattedSuggestions);

    if (this.suggestionItemList().length) {
      this._showSuggestion();
    } else this.closeSuggestion();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.enterKeyPress.emit();
      this.closeSuggestion();
    }
  }

  private _showSuggestion(): void {
    if (this.overlayRef) return;
    const origin = this.hostFormField;
    if (!origin) return;

    this.overlayRef = this._overlay.create(
      this._getOverlayConfig(origin, origin.clientWidth)
    );
    this.overlayRef.attach(this.suggestionPortal);
    this._handleOutsidePointerEvent();
    this._handleSuggestionClickEvent();
  }

  private _getOverlayConfig(origin: HTMLElement, width: number): OverlayConfig {
    return new OverlayConfig({
      width,
      hasBackdrop: true,
      backdropClass: 'suggestion-backdrop',
      positionStrategy: this._getOverlayPosition(origin),
      scrollStrategy: this._overlay.scrollStrategies.reposition({
        autoClose: true
      })
    });
  }

  private _getOverlayPosition(origin: HTMLElement): PositionStrategy {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        }
      ])
      .withPush(false);

    return positionStrategy;
  }

  private _handleOutsidePointerEvent(): void {
    this.overlayRef
      ?.outsidePointerEvents()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: e => {
            const targetedElement = e.target as HTMLElement;
            if (!this.hostFormField?.contains(targetedElement)) {
              this.closeSuggestion();
            }
          }
        })
      )
      .subscribe();
  }

  private _handleSuggestionClickEvent(): void {
    fromEvent(this.overlayRef!.overlayElement, 'click')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: e => {
            const targetedElement = e.target as HTMLElement;
            const suggestionOption: HTMLElement | null =
              targetedElement.closest('.suggestion-option');
            if (suggestionOption) {
              const value = suggestionOption.innerText;
              this.textField.value = value;
              this.suggestionSelect.emit(value);
              this.textField.dispatchEvent(this.inputEvent);
              this._addClassToHostElement('input-filled');
              this.closeSuggestion();
            }
          }
        })
      )
      .subscribe();
  }

  private _handleDocumentClick(): void {
    fromEvent(document, 'click')
      .pipe(
        take(1),
        tap({
          next: e => {
            const target = e.target as Element;
            const isInsideFormFieldHost = this.hostFormField?.contains(target);
            const isInsideOverlay =
              this.overlayRef?.overlayElement.contains(target);

            if (isInsideFormFieldHost || isInsideOverlay) {
              this.textField.focus();
              this._changeDetectorRef.detectChanges();
            } else {
              this._removeClassFromHostElement('input-focused');
              this.closeSuggestion();
            }
          }
        })
      )
      .subscribe();
  }

  private _addClassToHostElement(value: string): void {
    this._renderer.addClass(this.host, value);
  }

  private _inputValueChanges(): Observable<string> {
    return fromEvent(this.textField, 'input').pipe(
      map(event => (event.target as HTMLInputElement).value),
      tap({
        next: value => {
          value
            ? this._addClassToHostElement('input-filled')
            : this._removeClassFromHostElement('input-filled');

          this.suggestionItemList.set(this._formattedSuggestions);

          if (this.suggestionItemList().length) {
            this._showSuggestion();
          } else this.closeSuggestion();
        }
      })
    );
  }

  private _inputBlur(): Observable<Event> {
    return fromEvent(this.textField, 'blur').pipe(
      tap({
        next: () => {
          if (!document.hasFocus()) {
            this._removeClassFromHostElement('input-focused');
            this.closeSuggestion();
            return;
          }
          this._handleDocumentClick();
        }
      })
    );
  }

  private _removeClassFromHostElement(value: string): void {
    this._renderer.removeClass(this.host, value);
  }

  private _replaceHostElementClass(oldValue: string, newValue: string): void {
    this._removeClassFromHostElement(oldValue);
    this._addClassToHostElement(newValue);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _checkControlValidity(control: AbstractControl<any, any>): void {
    const controlStillInvalid = this.host.classList.contains('ng-invalid');
    const controlStillValid = this.host.classList.contains('ng-valid');

    if (control.valid && controlStillInvalid) {
      this._replaceHostElementClass('ng-invalid', 'ng-valid');
    }

    if (!control.valid && controlStillValid) {
      this._replaceHostElementClass('ng-valid', 'ng-invalid');
    }
  }
}
